import { useState, useEffect } from "react";

// PrimeReact
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";

// Toolkit
import { themeClasses as T, C } from "sj-ui-toolkit/src/atlas/utils/themeClasses";

const groups = [
  {
    name: "Security Journey domains",
    domains: [
      { displayDomain: "my.securityjourney.com", requestedDomains: ["my.securityjourney.com"] },
      {
        displayDomain: "*.my.securityjourney.com",
        requestedDomains: ["app.sb.my.securityjourney.com"],
      },
      { displayDomain: "auth.hackedu.com", requestedDomains: ["auth.hackedu.com"] },
    ],
  },
  {
    name: "Video domains",
    domains: [
      { displayDomain: "vimeo.com", requestedDomains: ["vimeo.com"] },
      {
        displayDomain: "*.vimeo.com",
        requestedDomains: [
          "player.vimeo.com",
          "f.vimeocdn.com",
          "i.vimeocdn.com",
          "vod-adaptive-ak.vimeocdn.com",
        ],
      },
    ],
  },
  {
    name: "Platform tool domains",
    domains: [
      {
        displayDomain: "*.launchdarkly.com",
        requestedDomains: [
          "app.launchdarkly.com",
          "clientstream.launchdarkly.com",
          "events.launchdarkly.com",
        ],
      },
      {
        displayDomain: "browser-intake-datadoghq.com",
        requestedDomains: ["browser-intake-datadoghq.com"],
      },
    ],
  },
];

const allRequestedDomains = groups.reduce((acc, group) => {
  group.domains.forEach(({ requestedDomains }) => {
    requestedDomains.forEach(domain => {
      if (!acc.includes(domain)) {
        acc.push(domain);
      }
    });
  });
  return acc;
}, []);

const TestConnection = () => {
  const [status, setStatus] = useState({});

  const testDomain = async domain => {
    try {
      const startTime = performance.now();
      await fetch(`https://${domain}`, {
        mode: "no-cors",
        cache: "no-cache",
      });
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);

      setStatus(prev => ({
        ...prev,
        [domain]: {
          isConnected: true,
          latency,
          timestamp: new Date().toISOString(),
        },
      }));
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        [domain]: {
          isConnected: false,
          error: error.message,
          timestamp: new Date().toISOString(),
        },
      }));
    }
  };

  useEffect(() => {
    allRequestedDomains.forEach(domain => testDomain(domain));
    // Test connections every 30 seconds
    const interval = setInterval(() => {
      allRequestedDomains.forEach(domain => testDomain(domain));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const isDisplayDomainConnected = requestedDomains =>
    requestedDomains.every(domain => status[domain]?.isConnected);

  return (
    <main className={C(T.largePaddedContainer, "h-full")} aria-label="Test Connection">
      <Card className="mb-4 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <i className="pi pi-wifi text-2xl" />
          <h2 className="atlas-h3 m-0">Testing Browser Connection</h2>
        </div>

        <div className="flex flex-col gap-4">
          {groups.map(group => (
            <div key={group.name} className={C(T.bgElevation1, "p-4 rounded")}>
              <h4 className="atlas-h5 mb-3">{group.name}</h4>
              <div className="flex flex-col gap-2">
                {group.domains.map(({ displayDomain, requestedDomains }) => {
                  const isConnected = isDisplayDomainConnected(requestedDomains);
                  const maxLatency = isConnected
                    ? Math.max(...requestedDomains.map(domain => status[domain]?.latency || 0))
                    : 0;

                  return (
                    <div key={displayDomain} className="flex items-center gap-2 body-md">
                      {isConnected ? (
                        <i className="pi pi-link text-green-500" title="Connected" />
                      ) : (
                        <i className="pi pi-link error-text" title="Not Connected" />
                      )}
                      <span>{displayDomain}</span>
                      {isConnected && (
                        <Tag value={`${maxLatency}ms`} severity="success" className="ml-2" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </main>
  );
};

export default TestConnection;
